import { Link } from 'react-router-dom';
import type { SpecialistSummary } from '@/types/domain';
import { Card } from '@/components/atoms/Card';
import { Avatar } from '@/components/atoms/Avatar';
import { Badge } from '@/components/atoms/Badge';
import { Icon } from '@/components/atoms/Icon';
import { badgeLabel } from '@/i18n/badges';
import styles from './SpecialistCard.module.css';

export interface SpecialistCardProps {
  specialist: SpecialistSummary;
}

export function SpecialistCard({ specialist }: SpecialistCardProps): JSX.Element {
  const primaryCategory = specialist.categories[0]?.name_ua ?? '';
  const city = specialist.city?.name_ua ?? '';
  // F-170 B3.1: prefer the self-authored title, fall back to the primary
  // category so the line is never empty (better-than-blank fallback).
  const subline = (specialist.title?.trim() || primaryCategory).trim();
  return (
    <Link
      to={`/u/${specialist.slug}`}
      className={styles.card}
      style={{ display: 'block' }}
      aria-label={`Профіль ${specialist.first_name}`}
    >
      <Card interactive>
        <div className={styles.card}>
          <Avatar
            src={specialist.avatar_url}
            name={specialist.first_name}
            size={56}
            presence={specialist.presence_status}
          />
          <div className={styles.body}>
            <div className={styles.head}>
              <span className={styles.name}>{specialist.first_name}</span>
              <span className={styles.score}>
                {(() => {
                  // F-190 QA B8: `· 9` next to the score read as "score
                  // out of 9" until you knew it was the review count.
                  // Pluralised UA suffix (відгук / відгуки / відгуків)
                  // makes the unit explicit.
                  const n = specialist.review_count ?? 0;
                  if (n === 0) return 'Новий';
                  const mod10 = n % 10;
                  const mod100 = n % 100;
                  const word =
                    mod10 === 1 && mod100 !== 11
                      ? 'відгук'
                      : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)
                        ? 'відгуки'
                        : 'відгуків';
                  return `${specialist.overall_score ?? 0}/100 · ${n} ${word}`;
                })()}
              </span>
            </div>
            {subline ? <span className={styles.title}>{subline}</span> : null}
            {specialist.is_test_persona && import.meta.env.DEV ? (
              // F-180 D6: ТЕСТОВИЙ pill is dev-only — real users on prod
              // shouldn't see seeded-persona scaffolding bleed into the
              // catalogue. Lift the gate once real specialists onboard.
              <span className={styles.testPill} aria-label="Тестовий профіль">
                <Icon name="flask" size={12} />
                ТЕСТОВИЙ
              </span>
            ) : null}
            <span className={styles.bio}>{specialist.bio_excerpt}</span>
            <div className={styles.meta}>
              {(() => {
                // F-160 (C8) + F-170 (B2.6 / B3.1): only emit a `·`
                // separator when there's actually a left-hand sibling.
                // Languages dropped from the meta row as part of the
                // UA-only MVP cut. When `title` is present, the meta row
                // can drop down to just city — the category is already
                // implied by the title line above (or matches it).
                const parts: string[] = [];
                if (specialist.title?.trim()) {
                  // Title already covers "what". Show only city here.
                  if (city) parts.push(city);
                } else {
                  if (primaryCategory) parts.push(primaryCategory);
                  if (city) parts.push(city);
                }
                return parts.map((p, i) => (
                  <span key={i}>{i === 0 ? p : `· ${p}`}</span>
                ));
              })()}
            </div>
            {(specialist.badges ?? []).length ? (
              <div className={styles.badgesRow}>
                {(specialist.badges ?? []).slice(0, 3).map((b) => (
                  <Badge key={b} variant="accent">
                    {badgeLabel(b)}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </Card>
    </Link>
  );
}
